import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText, Paper, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const ColumnOrderDialog = ({ open, onClose, columns, onSave }) => {
  const { t } = useTranslation();
  const [order, setOrder] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);

  // Aktualisiere die Reihenfolge, wenn sich die Spalten ändern
  useEffect(() => {
    if (open && columns) {
      // Validiere und filtere Spalten mit gültigen field-Properties
      const validColumns = columns.filter(col => {
        const isValid = col && col.field && typeof col.field === 'string';
        if (!isValid) {
          console.warn('ColumnOrderDialog: Ungültige Spalte gefiltert:', col);
        }
        return isValid;
      });
      
      if (validColumns.length > 0) {
        // Sortiere die Spalten nach ihrer aktuellen Reihenfolge (order property)
        const sortedColumns = [...validColumns].sort((a, b) => {
          const orderA = a.order !== undefined ? a.order : 0;
          const orderB = b.order !== undefined ? b.order : 0;
          return orderA - orderB;
        });
        
        setOrder(sortedColumns);
      } else {
        console.error('ColumnOrderDialog: Keine gültigen Spalten gefunden');
        setOrder([]);
      }
    }
  }, [columns, open]);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      return;
    }

    const newOrder = [...order];
    const [draggedItem] = newOrder.splice(draggedIndex, 1);
    
    if (!draggedItem || !draggedItem.field) {
      console.error('ColumnOrderDialog - handleDrop - Ungültiges draggedItem:', draggedItem);
      return;
    }
    
    newOrder.splice(dropIndex, 0, draggedItem);
    
    // Aktualisiere die order property für alle Spalten
    const updatedOrder = newOrder.map((col, idx) => ({
      ...col,
      order: idx
    }));
    
    setOrder(updatedOrder);
    setDraggedIndex(null);
    setDropIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDropIndex(null);
  };

  const handleSave = () => {
    // Validiere die Reihenfolge vor dem Speichern
    const validOrder = order.filter(col => col && col.field);
    
    if (validOrder.length === 0) {
      console.error('ColumnOrderDialog: Keine gültigen Spalten zum Speichern');
      return;
    }
    
    // Aktualisiere die order property basierend auf der aktuellen Position
    const updatedOrder = validOrder.map((col, idx) => ({
      ...col,
      order: idx
    }));
    
    onSave(updatedOrder);
    onClose();
  };

    return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('common.columnOrder', 'Spaltenreihenfolge ändern')}</DialogTitle>
      <DialogContent>
        {order.length > 0 ? (
          <List>
            {order.map((col, idx) => {
              // Zusätzliche Validierung für jedes Element
              if (!col || !col.field) {
                console.warn('Invalid column object:', col);
                return null;
              }
              
              return (
                <Box key={col.field}>
                  {/* Orange Drop-Indikator Linie */}
                  {dropIndex === idx && draggedIndex !== null && draggedIndex !== idx && (
                    <Box
                      sx={{
                        height: '3px',
                        backgroundColor: 'var(--rd-accent)',
                        margin: '4px 0',
                        borderRadius: '2px',
                        boxShadow: '0 0 4px color-mix(in srgb, var(--rd-accent) 60%, transparent)'
                      }}
                    />
                  )}
                  
                  <ListItem
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDrop={(e) => handleDrop(e, idx)}
                    onDragEnd={handleDragEnd}
                    component={Paper}
                    sx={{ 
                      mb: 1, 
                      boxShadow: draggedIndex === idx ? 3 : 1,
                      cursor: 'grab',
                      '&:active': { cursor: 'grabbing' },
                      opacity: draggedIndex === idx ? 0.5 : 1,
                      border: dropIndex === idx && draggedIndex !== null && draggedIndex !== idx ? '2px solid var(--rd-accent)' : 'none'
                    }}
                  >
                    <ListItemText primary={t(`taskColumns.${col.field}`, col.headerName || col.field)} />
                  </ListItem>
                </Box>
              );
            })}
          </List>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 100 }}>
            <Typography>{t('common.loading', 'Loading...')}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} className="button-cancel">{t('common.cancel', 'Abbrechen')}</Button>
        <Button onClick={handleSave} className="button">{t('common.save', 'Speichern')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnOrderDialog; 